import { Button, Icon, useIsMobile } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbPlus } from "react-icons/tb";
import z from "zod";

import { PageActions } from "@/components/page-actions";
import { CreateTeamModal } from "@/components/teams/create-team-modal";
import { DeleteTeamDialog } from "@/components/teams/delete-team-modal";
import { EditTeamModal } from "@/components/teams/edit-team-modal";
import { TeamDetailsCard } from "@/components/teams/team-details-card";
import { TeamList } from "@/components/teams/team-list";
import { SearchBar } from "@/components/ui/search-bar";
import { PAGINATION } from "@/constants/pagination";
import { useTeamsPaginated } from "@/hooks/queries/team/use-team-queries";
import { useTeamManagement } from "@/hooks/use-team-management";
import {
    TEAM_LIST_PANEL_WIDTH_CLASS,
    TEAM_TOOLBAR_SEARCH_WIDTH_CLASS,
} from "@/pages/management/teams/constants/teams-page.constants";
import { createPaginationProps } from "@/types/pagination.types";
import { Team } from "@/types/team.types";
import { DEFAULT_DEBOUNCE_MS } from "@/utils/debounce";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

const ManagementTeamsQueryParamsSchema = z.object({
    teamId: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.MANAGEMENT_TEAMS)({
    component: ManagementTeams,
    staticData: {
        config: {
            breadcrumb: {
                title: "teams",
                to: APP_ROUTES_TO.MANAGEMENT_TEAMS,
            },
            title: "teams",
            layout: { scroll: false },
        },
    },
    validateSearch: searchValidator(ManagementTeamsQueryParamsSchema),
});

function ManagementTeams() {
    const { t } = useTranslation();
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();
    const isMobile = useIsMobile();

    const [currentTeamsPage, setCurrentTeamsPage] = useState<number>(PAGINATION.DEFAULT_PAGE);
    const [teamSearchDraft, setTeamSearchDraft] = useState("");
    const [teamSearchQuery, setTeamSearchQuery] = useState("");

    const { data: teamsResponse, isLoading: isLoadingTeams } = useTeamsPaginated({
        page: currentTeamsPage,
        search: teamSearchQuery,
    });

    const selectedTeamId = searchParams.teamId;

    const {
        selectedTeam,
        isEditTeamDialogOpen,
        editTeamName,
        editTeamColor,
        editTeamSortOrder,
        showEditTeamColorPicker,
        tempTeamMembers,
        isCreatingTeam,
        newTeamName,
        newTeamColor,
        showColorPicker,
        showDeleteDialog,
        teamToDelete,
        userSearchQuery,
        isUpdatingMember,
        teamNameError,
        isValidatingName,
        currentPage,
        isShaking,
        unassignedUsersData,
        createTeamMutation,
        updateTeamMutation,
        deleteTeamMutation,
        handlePageChange,
        removeTeam,
        handleDeleteClick,
        onTeamClickHandler,
        openEditTeamDialog,
        closeEditTeamDialog,
        saveTeamChanges,
        onTeamMemberRoleChange,
        handleCreateTeam,
        handleCreateTeamKeyDown,
        resetCreateTeamForm,
        setUserSearchQuery,
        setIsCreatingTeam,
        setNewTeamName,
        setNewTeamColor,
        setShowColorPicker,
        setEditTeamName,
        setEditTeamColor,
        setEditTeamSortOrder,
        setShowEditTeamColorPicker,
        setShowDeleteDialog,
        setSelectedTeam,
    } = useTeamManagement({ teamsList: teamsResponse.data });

    useEffect(() => {
        if (!selectedTeamId) {
            if (selectedTeam) {
                setSelectedTeam(null);
            }
            return;
        }

        const found = teamsResponse.data.find((team) => team.id === selectedTeamId);
        if (found && selectedTeam?.id !== found.id) {
            onTeamClickHandler(found);
        }
    }, [selectedTeamId, teamsResponse.data, selectedTeam, onTeamClickHandler]);

    const handleTeamClick = (team: Team) => {
        navigate({ to: Route.to, search: { teamId: team.id } });
        onTeamClickHandler(team);
    };

    const handleClearSelectedTeam = useCallback(() => {
        navigate({ to: Route.to, search: {}, replace: true });
        setSelectedTeam(null);
    }, [navigate]);

    const handleTeamsPageChange = (page: number) => {
        setCurrentTeamsPage(page);
    };

    const handleTeamSearchChange = (query: string) => {
        setTeamSearchQuery(query);
        setCurrentTeamsPage(PAGINATION.DEFAULT_PAGE);
    };

    const handleDeleteTeam = () => {
        if (!teamToDelete) return;
        removeTeam(teamToDelete);
        if (selectedTeamId === teamToDelete) {
            navigate({ to: Route.to, search: {}, replace: true });
        }
    };

    const showTeamList = !isMobile || !selectedTeam;
    const showTeamDetails = !isMobile || Boolean(selectedTeam);

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col">
            <PageActions>
                <div className={TEAM_TOOLBAR_SEARCH_WIDTH_CLASS}>
                    <SearchBar
                        value={teamSearchDraft}
                        onChange={setTeamSearchDraft}
                        onDebouncedChange={handleTeamSearchChange}
                        debounceMs={DEFAULT_DEBOUNCE_MS}
                        placeholder={t("search-teams")}
                        className="[&_input]:h-9"
                    />
                </div>
                <Button
                    type="button"
                    onClick={() => setIsCreatingTeam(true)}
                    aria-label={t("create_team")}
                    disabled={createTeamMutation.isPending}
                >
                    <Icon icon={TbPlus} />
                    {t("create_team")}
                </Button>
            </PageActions>

            <div className="flex min-h-0 flex-1 gap-4">
                {showTeamList ? (
                    <div className={TEAM_LIST_PANEL_WIDTH_CLASS}>
                        <TeamList
                            teams={teamsResponse.data}
                            selectedTeam={selectedTeam}
                            onTeamClick={handleTeamClick}
                            isLoading={isLoadingTeams}
                            isShaking={isShaking}
                            hasSearchQuery={Boolean(teamSearchQuery.trim())}
                            pagination={createPaginationProps({
                                response: teamsResponse,
                                onPageChange: handleTeamsPageChange,
                                isLoading: isLoadingTeams,
                            })}
                        />
                    </div>
                ) : null}
                {showTeamDetails ? (
                    <div className="h-full min-h-0 min-w-0 flex-1">
                        <TeamDetailsCard
                            selectedTeam={selectedTeam}
                            tempTeamMembers={tempTeamMembers}
                            userSearchQuery={userSearchQuery}
                            currentPage={currentPage}
                            pageSize={PAGINATION.PAGE_SIZE}
                            isUpdatingMember={isUpdatingMember}
                            updateTeamMutation={updateTeamMutation}
                            deleteTeamMutation={deleteTeamMutation}
                            onOpenEditDialog={openEditTeamDialog}
                            onHandleDeleteClick={handleDeleteClick}
                            onUserSearchChange={setUserSearchQuery}
                            onTeamMemberRoleChange={onTeamMemberRoleChange}
                            onPageChange={handlePageChange}
                            unassignedUsersData={unassignedUsersData || { users: [], total: 0 }}
                            onBack={isMobile ? handleClearSelectedTeam : undefined}
                        />
                    </div>
                ) : null}
            </div>

            <CreateTeamModal
                isOpen={isCreatingTeam}
                onClose={resetCreateTeamForm}
                newTeamName={newTeamName}
                setNewTeamName={setNewTeamName}
                newTeamColor={newTeamColor}
                setNewTeamColor={setNewTeamColor}
                showColorPicker={showColorPicker}
                setShowColorPicker={setShowColorPicker}
                isShaking={isShaking}
                onCreateTeam={handleCreateTeam}
                onCreateTeamKeyDown={handleCreateTeamKeyDown}
                isCreatingTeamLoading={createTeamMutation.isPending}
            />

            <EditTeamModal
                isOpen={isEditTeamDialogOpen}
                onClose={closeEditTeamDialog}
                teamName={editTeamName}
                onTeamNameChange={setEditTeamName}
                teamColor={editTeamColor}
                onTeamColorChange={setEditTeamColor}
                teamSortOrder={editTeamSortOrder}
                onTeamSortOrderChange={setEditTeamSortOrder}
                showColorPicker={showEditTeamColorPicker}
                setShowColorPicker={setShowEditTeamColorPicker}
                teamNameError={teamNameError}
                isValidatingName={isValidatingName}
                isShaking={isShaking}
                onSave={saveTeamChanges}
                isSaving={updateTeamMutation.isPending}
            />

            <DeleteTeamDialog
                isOpen={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                teamName={selectedTeam?.name}
                onDelete={handleDeleteTeam}
                isDeletingTeam={deleteTeamMutation.isPending}
            />
        </div>
    );
}
